import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Fieldset } from 'primereact/fieldset'
import {
  Badge,
  CustomCheckbox,
  CustomDataTableHeader,
  CustomDropdown,
  CustomMultiSelect,
  CustomPanel,
  PageHeader,
  Periodo,
  ThreeDots,
} from '../../../../components'
import { tableCellCentered } from '../../../../assets/js/styles'
import * as colors from '../../../../assets/js/colors'
import {
  hideLoadingBox,
  showLoadingBox,
  showMessageBox,
} from '../../../../store/actions/system.actions'
import { errorHandler } from '../../../../services/util/error.service'
import { getPeriodoMesAtual } from '../../../../services/util/date.service'
import { getEmissoresByTps } from '../../../../services/api/https/settings/emissor-cte.http'
import {
  getCiot,
  getCiots,
  postConsultarCiot,
} from '../../../../services/api/https/deliveries/ciot.http'
import ModalAcaoCiot from './ModalAcaoCiot'
import ModalDeclararCiot from './ModalDeclararCiot'
import ModalDetalhesCiot from './ModalDetalhesCiot'
import ModalRetificarCiot from './ModalRetificarCiot'
import {
  BadgeStatusCiot,
  CIOT_FRACIONADA,
  OPCOES_STATUS_CIOT,
  STATUS_CIOT,
  estiloDicaCiot,
  formatarDataHora,
  formatarDataIso,
  formatarMoeda,
  rotuloCiot,
} from './ciot.util'

const MENU = {
  DETALHES: 'Detalhes',
  CONSULTAR: 'Consultar na ANTT',
  DECLARAR: 'Declarar',
  ENCERRAR: 'Encerrar',
  RETIFICAR: 'Retificar',
  CANCELAR: 'Cancelar',
}

const declaradoSemMdfe = ciot => ciot.status === 'declarado' && !Number(ciot.vinculado)

function itensMenu(ciot) {
  const itens = [MENU.DETALHES]
  if (['gerado', 'pendente', 'recusado', 'declarado'].includes(ciot.status))
    itens.push(MENU.CONSULTAR)
  if (['gerado', 'recusado'].includes(ciot.status) && ciot.fleet_id) itens.push(MENU.DECLARAR)
  if (ciot.status === 'declarado') itens.push(MENU.ENCERRAR)
  if (ciot.status === 'declarado' && Number(ciot.tipo_operacao) === CIOT_FRACIONADA)
    itens.push(MENU.RETIFICAR)
  if (['gerado', 'recusado', 'declarado'].includes(ciot.status)) itens.push(MENU.CANCELAR)
  return itens
}

function GestaoCiot({ dispatch, selectedTp, history }) {
  const [periodo, setPeriodo] = useState(getPeriodoMesAtual())
  const [ciots, setCiots] = useState([])
  const [emissores, setEmissores] = useState([])
  const [filtros, setFiltros] = useState({ emissor: null, status: [], semMdfe: false })
  const [globalFilter, setGlobalFilter] = useState('')
  const [modalAcao, setModalAcao] = useState({ visible: false, acao: null, ciot: null })
  const [modalDetalhes, setModalDetalhes] = useState({ visible: false, ciotId: null })
  const [modalDeclarar, setModalDeclarar] = useState({ visible: false, ciot: null })
  const [modalRetificar, setModalRetificar] = useState({ visible: false, ciot: null })
  const declaracaoAberta = useRef(false)

  const onError = error => dispatch(showMessageBox(null, errorHandler(error)))

  const carregar = async (pInicial = periodo.dataInicial, pFinal = periodo.dataFinal) => {
    if (!selectedTp) {
      onError('Selecione um TP')
      return
    }
    dispatch(showLoadingBox('Aguarde...', 'Buscando CIOTs'))
    try {
      setCiots(await getCiots(selectedTp, pInicial, pFinal))
      setPeriodo({ dataInicial: pInicial, dataFinal: pFinal })
    } catch (error) {
      onError(error)
    } finally {
      dispatch(hideLoadingBox())
    }
  }

  useEffect(() => {
    carregar()
    if (selectedTp) getEmissoresByTps(selectedTp).then(setEmissores).catch(onError)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTp])

  // O Dialog chama onClose também ao ser escondido; a lista recarrega uma vez quando a declaração fecha
  useEffect(() => {
    if (declaracaoAberta.current && !modalDeclarar.visible) carregar()
    declaracaoAberta.current = modalDeclarar.visible
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalDeclarar.visible])

  const concluir = mensagem => {
    dispatch(showMessageBox(mensagem, null))
    carregar()
  }

  const onMenuClick = async (item, ciot) => {
    try {
      if (item === MENU.DETALHES) setModalDetalhes({ visible: true, ciotId: ciot.id })
      else if (item === MENU.ENCERRAR) setModalAcao({ visible: true, acao: 'encerrar', ciot })
      else if (item === MENU.CANCELAR) setModalAcao({ visible: true, acao: 'cancelar', ciot })
      else if (item === MENU.DECLARAR) setModalDeclarar({ visible: true, ciot })
      else if (item === MENU.RETIFICAR)
        setModalRetificar({ visible: true, ciot: await getCiot(ciot.id) })
      else if (item === MENU.CONSULTAR) {
        dispatch(showLoadingBox('Aguarde...', 'Consultando a ANTT'))
        const atualizado = await postConsultarCiot(ciot.id)
        dispatch(hideLoadingBox())
        const consulta = atualizado.consulta || { encontrado: false, mensagens: [] }
        if (consulta.encontrado)
          concluir(`CIOT ${rotuloCiot(atualizado)} consta como declarado na ANTT`)
        else {
          onError(
            `A ANTT não confirmou a declaração do CIOT ${atualizado.numero}` +
              (consulta.mensagens.length ? `: ${consulta.mensagens.join(' ')}` : ''),
          )
          carregar()
        }
      }
    } catch (error) {
      dispatch(hideLoadingBox())
      onError(error)
    }
  }

  const filtrados = ciots
    .filter(ciot => {
      if (filtros.emissor && ciot.emissor_id !== filtros.emissor.id) return false
      if (filtros.status.length && !filtros.status.includes(ciot.status)) return false
      if (filtros.semMdfe && !declaradoSemMdfe(ciot)) return false
      return true
    })
    // A pesquisa da tabela só procura nas colunas com field: o texto exibido vira campo da linha
    .map(ciot => ({
      ...ciot,
      rotulo: rotuloCiot(ciot),
      status_nome: STATUS_CIOT[ciot.status] ? STATUS_CIOT[ciot.status].label : ciot.status,
      tipo_nome: ciot.tipo_operacao
        ? Number(ciot.tipo_operacao) === CIOT_FRACIONADA
          ? 'Fracionada'
          : 'Lotação'
        : '-',
      frete_texto: formatarMoeda(ciot.valor_frete),
      viagem_texto: ciot.data_inicio_viagem
        ? `${formatarDataIso(ciot.data_inicio_viagem)} a ${formatarDataIso(ciot.data_fim_viagem)}`
        : '-',
      gerado_texto: `${formatarDataHora(ciot.created_at)} ${ciot.gerado_por || ''}`,
    }))
  const qtdSemMdfe = ciots.filter(declaradoSemMdfe).length
  const emissorDoCiot = ciot => emissores.find(emissor => emissor.id === ciot.emissor_id) || null

  return (
    <>
      <PageHeader>3.19 - Gestão de MDFe's › Gestão de CIOT</PageHeader>
      <div style={{ marginTop: 20 }}>
        <CustomPanel showBtnNovo={false} onVoltar={history.goBack}>
          <div className="p-grid">
            <div className="p-col-3">
              <Periodo
                periodoInicial={periodo.dataInicial}
                periodoFinal={periodo.dataFinal}
                onPesquisar={(pInicial, pFinal) => carregar(pInicial, pFinal)}
              />
            </div>
            <div className="p-col-9" style={{ ...estiloDicaCiot, paddingTop: 40 }}>
              CIOTs gerados e declarados na ANTT pelo sistema, com o certificado de cada emissor. Os
              MDFes continuam na 3.19; cancelar ou encerrar o MDFe não interfere no CIOT, e
              vice-versa.
            </div>

            <Fieldset legend="Filtro" toggleable collapsed={true} className="p-col-12 p-mt-2">
              <div className="p-grid">
                <div className="p-col-6 p-md-4">
                  <CustomDropdown
                    optionLabel="razao_social"
                    options={emissores}
                    value={filtros.emissor}
                    label="Emissor"
                    showClear={true}
                    filter={true}
                    onChange={value => setFiltros(prev => ({ ...prev, emissor: value }))}
                  />
                </div>
                <div className="p-col-6 p-md-4">
                  <CustomMultiSelect
                    label="Status"
                    value={filtros.status}
                    options={OPCOES_STATUS_CIOT}
                    optionLabel="label"
                    onChange={value => setFiltros(prev => ({ ...prev, status: value || [] }))}
                  />
                </div>
              </div>
            </Fieldset>

            <div className="p-col-12" style={{ marginTop: 15, marginBottom: 10 }}>
              <CustomCheckbox
                checked={filtros.semMdfe}
                onChange={value => setFiltros(prev => ({ ...prev, semMdfe: value }))}
              >
                Só declarados sem MDFe ({qtdSemMdfe})
              </CustomCheckbox>
            </div>

            <div className="p-col-12">
              <DataTable
                value={filtrados}
                paginator={true}
                rows={10}
                emptyMessage="Nenhum CIOT no período"
                globalFilter={globalFilter}
                header={<CustomDataTableHeader onChange={setGlobalFilter} />}
              >
                <Column field="rotulo" header="CIOT" style={tableCellCentered(170)} />
                <Column
                  field="status_nome"
                  header="Status"
                  style={tableCellCentered(130)}
                  body={ciot => (
                    <>
                      <BadgeStatusCiot status={ciot.status} />
                      {declaradoSemMdfe(ciot) && (
                        <div style={{ marginTop: 3 }}>
                          <Badge color={colors.laranja}>Sem MDFe</Badge>
                        </div>
                      )}
                    </>
                  )}
                />
                <Column field="tipo_nome" header="Tipo" style={tableCellCentered(90)} />
                <Column field="placa" header="Placa" style={tableCellCentered(90)} />
                <Column field="emissor_razao_social" header="Emitente" />
                <Column field="frete_texto" header="Frete" style={tableCellCentered(110)} />
                <Column field="viagem_texto" header="Viagem" style={tableCellCentered(120)} />
                <Column field="mdfes" header="MDFes" style={tableCellCentered(140)} />
                <Column
                  field="mensagem"
                  header="Mensagem"
                  body={ciot => (
                    <span
                      style={{
                        fontSize: '0.8rem',
                        color: ciot.status === 'recusado' ? '#721C24' : '#666',
                      }}
                    >
                      {ciot.mensagem}
                    </span>
                  )}
                />
                <Column
                  field="gerado_texto"
                  header="Gerado em"
                  style={tableCellCentered(140)}
                  body={ciot => (
                    <>
                      {formatarDataHora(ciot.created_at)}
                      <div style={{ fontSize: '0.75rem', color: '#888' }}>{ciot.gerado_por}</div>
                    </>
                  )}
                />
                <Column
                  header="Menu"
                  style={tableCellCentered(70)}
                  body={ciot => (
                    <ThreeDots items={itensMenu(ciot)} onClick={item => onMenuClick(item, ciot)} />
                  )}
                />
              </DataTable>
            </div>
          </div>
        </CustomPanel>
      </div>

      <ModalAcaoCiot
        visible={modalAcao.visible}
        acao={modalAcao.acao}
        ciot={modalAcao.ciot}
        onClose={() => setModalAcao({ visible: false, acao: null, ciot: null })}
        onConcluido={(_, mensagem) => {
          setModalAcao({ visible: false, acao: null, ciot: null })
          concluir(mensagem)
        }}
        onError={onError}
      />
      <ModalDetalhesCiot
        visible={modalDetalhes.visible}
        ciotId={modalDetalhes.ciotId}
        onClose={() => setModalDetalhes({ visible: false, ciotId: null })}
        onError={onError}
      />
      <ModalDeclararCiot
        visible={modalDeclarar.visible}
        ciot={modalDeclarar.ciot}
        emissor={modalDeclarar.ciot ? emissorDoCiot(modalDeclarar.ciot) : null}
        contexto={{ fleetId: modalDeclarar.ciot ? modalDeclarar.ciot.fleet_id : null }}
        onClose={() => setModalDeclarar({ visible: false, ciot: null })}
        onDeclarado={ciot => {
          setModalDeclarar({ visible: false, ciot: null })
          dispatch(showMessageBox(`CIOT ${rotuloCiot(ciot)} declarado na ANTT`, null))
        }}
        onError={onError}
      />
      <ModalRetificarCiot
        visible={modalRetificar.visible}
        ciot={modalRetificar.ciot}
        onClose={() => setModalRetificar({ visible: false, ciot: null })}
        onConcluido={(_, mensagem) => {
          setModalRetificar({ visible: false, ciot: null })
          concluir(mensagem)
        }}
        onError={onError}
      />
    </>
  )
}

const mapToProps = ({ system: { selectedTp } }) => ({ selectedTp })
export default connect(mapToProps)(withRouter(GestaoCiot))
